
'use client';

import Image from 'next/image';
import { Button } from '@/components/ui/button';
import { ArrowRight, ShieldCheck, Scale, Landmark, Menu, X } from 'lucide-react';
import { useState } from 'react';

export function CorporateLuxuryHybridHomepage() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  return (
    <div className="bg-white text-gray-900 font-sans">
      <header className="bg-white/80 backdrop-blur-md sticky top-0 z-20 border-b border-gray-200">
        <div className="container mx-auto flex justify-between items-center p-6">
          <h2 className="text-2xl font-semibold tracking-wider">VALERIAN</h2>
          <nav className="hidden md:flex items-center gap-8 text-sm">
            <a href="#" className="text-gray-600 hover:text-black">
              Expertise
            </a>
            <a href="#" className="text-gray-600 hover:text-black">
              About Us
            </a>
            <a href="#" className="text-gray-600 hover:text-black">
              Insights
            </a>
            <Button variant="outline" className="rounded-none">
              Contact
            </Button>
          </nav>
          <div className="md:hidden">
            <Button
              size="icon"
              variant="ghost"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              {isMenuOpen ? <X /> : <Menu />}
            </Button>
          </div>
        </div>
        {isMenuOpen && (
          <div className="md:hidden bg-white/95 border-t">
            <nav className="container mx-auto flex flex-col items-start gap-4 p-6 text-lg">
              <a href="#" className="text-gray-600 hover:text-black">
                Expertise
              </a>
              <a href="#" className="text-gray-600 hover:text-black">
                About Us
              </a>
              <a href="#" className="text-gray-600 hover:text-black">
                Insights
              </a>
              <Button variant="outline" className="rounded-none mt-2 w-full">
                Contact
              </Button>
            </nav>
          </div>
        )}
      </header>
      <main>
        <section className="relative h-[80vh] flex items-center">
          <div className="absolute inset-0 bg-gradient-to-r from-white via-white/80 to-transparent z-10" />
          <Image
            src="https://picsum.photos/seed/corp-lux1/1800/1200"
            alt="Abstract image of a modern building's facade with gold accents"
            fill
            className="object-cover"
            priority
            data-ai-hint="modern architecture"
          />
          <div className="container mx-auto px-6 relative z-10">
            <h1 className="text-5xl md:text-7xl font-light leading-tight max-w-2xl">
              Navigating Complexity with Unrivaled Acumen.
            </h1>
            <p className="mt-6 text-lg text-gray-700 max-w-lg">
              Valerian is a premier advisory firm providing strategic counsel at
              the intersection of finance, law, and global markets.
            </p>
            <Button
              size="lg"
              className="mt-10 bg-gray-900 text-white hover:bg-gray-700 rounded-none px-10 py-7"
            >
              Our Services <ArrowRight className="ml-2" />
            </Button>
          </div>
        </section>

        <section className="py-16 md:py-24 bg-gray-50">
          <div className="container mx-auto px-6">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <h2 className="text-3xl md:text-4xl font-light">
                A Legacy of Excellence
              </h2>
              <p className="mt-4 text-gray-600">
                We offer a multidisciplinary approach to solve the most
                intricate challenges facing our clients, from global
                corporations to private equity.
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-8 text-left">
              <div className="p-8 border border-gray-200">
                <Landmark className="w-10 h-10 text-gray-700 mb-4" />
                <h3 className="font-semibold text-xl mb-2">
                  Corporate Finance
                </h3>
                <p className="text-gray-600">
                  Mergers & acquisitions, capital raising, and complex
                  restructuring with precision and discretion.
                </p>
              </div>
              <div className="p-8 border border-gray-200">
                <Scale className="w-10 h-10 text-gray-700 mb-4" />
                <h3 className="font-semibold text-xl mb-2">
                  Regulatory & Compliance
                </h3>
                <p className="text-gray-600">
                  Navigating the evolving landscape of international
                  regulations and compliance standards.
                </p>
              </div>
              <div className="p-8 border border-gray-200">
                <ShieldCheck className="w-10 h-10 text-gray-700 mb-4" />
                <h3 className="font-semibold text-xl mb-2">
                  Risk & Reputation
                </h3>
                <p className="text-gray-600">
                  Strategic crisis management, litigation support, and
                  long-term reputational advisory.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 md:py-24">
          <div className="container mx-auto px-6 grid md:grid-cols-2 gap-16 items-center">
            <div>
              <Image
                src="https://picsum.photos/seed/corp-lux2/1000/1200"
                alt="A sophisticated meeting room with dark wood and leather chairs"
                width={1000}
                height={1200}
                className="w-full h-auto"
                data-ai-hint="meeting room"
              />
            </div>
            <div className="max-w-lg">
              <h2 className="text-3xl md:text-4xl font-light">
                The Valerian Standard.
              </h2>
              <p className="mt-4 text-gray-600">
                Our firm is built on a foundation of integrity, intellectual
                rigor, and an unwavering commitment to client success. We act
                as trusted partners, providing clear, actionable intelligence
                in high-stakes situations.
              </p>
              <Button
                variant="link"
                className="mt-6 p-0 text-black group text-base"
              >
                Meet Our Leadership{' '}
                <ArrowRight className="ml-2 transition-transform group-hover:translate-x-1" />
              </Button>
            </div>
          </div>
        </section>

        <section className="bg-gray-900 text-white py-24">
          <div className="container mx-auto px-6 text-center">
            <h2 className="text-3xl md:text-4xl font-light">
              Discreet Consultation.
            </h2>
            <p className="mt-4 text-gray-300 max-w-xl mx-auto">
              To discuss a confidential matter, please contact our partners.
            </p>
            <Button
              variant="outline"
              className="mt-10 rounded-none uppercase tracking-widest px-10 py-7 border-gray-600 text-gray-300 hover:border-white hover:bg-transparent hover:text-white"
            >
              Inquire
            </Button>
          </div>
        </section>
      </main>
      <footer className="border-t border-gray-200 py-12 bg-white">
        <div className="container mx-auto px-6 flex flex-col md:flex-row justify-between items-center text-sm text-gray-500 gap-4">
          <p>&copy; {new Date().getFullYear()} Valerian Capital. All Rights Reserved.</p>
          <nav className="flex gap-6">
            <a href="#" className="hover:text-black">
              Legal Notice
            </a>
            <a href="#" className="hover:text-black">
              LinkedIn
            </a>
          </nav>
        </div>
      </footer>
    </div>
  );
}
